import {
  ConditionType,
  conditionTypeToString,
  InsightCondition,
} from '@toggle/toggle';
import { t } from 'i18next';
import { useNavigate } from 'react-router';

import { WORKING_LIST_ID } from '~/config/constants';
import { useFilterActions } from '~/hooks/use-filter-actions/useFilterActions';
import { useCompanyCompetitorWithThemes } from '~/hooks/use-knowledge-graph/useKnowledgeGraph';
import { useWorkingList } from '~/hooks/use-working-list/useWorkingList';
import { appPaths } from '~/routes/app-paths';
import { useWatchlist } from '~/stores/use-watchlist/useWatchlist';
import { getWatchlistEntitiesTags } from '~/stores/use-watchlist/useWatchlist.utils';
import { SelectedAssetItem } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/run-scenario/RunScenario';
import { useScenarioAssistant } from '~/widgets/scenario/components/scenario-assistant-modal/use-scenario-assistant/useScenarioAssistant';
import { SCENARIO_ASSET_FILTER_KEY } from '~/widgets/scenario/hooks/use-scenario-store/filter-utils';
import { useScenarioStore } from '~/widgets/scenario/hooks/use-scenario-store/useScenarioStore';

interface UseScenarioInsightsProps {
  entityTag: string;
  conditions: InsightCondition[];
}

export const useScenarioFromInsights = ({
  entityTag,
  conditions,
}: UseScenarioInsightsProps) => {
  const navigate = useNavigate();
  const { saveWorkingList } = useWorkingList();
  const { addFilterOption: addScenarioFilterOption } = useFilterActions({
    store: useScenarioStore,
    group: 'ASSET_FILTERS',
  });
  const { resetActiveFilterOptions: resetActiveConditionFilterOptions } =
    useFilterActions({
      store: useScenarioStore,
      group: 'SCENARIO_CONDITIONS',
    });
  const { resetConditions, manuallySetCustomConditions } = useScenarioAssistant(
    state => ({
      resetConditions: state.resetConditions,
      manuallySetCustomConditions: state.manuallySetCustomConditions,
    })
  );

  const { data: competitorsData } = useCompanyCompetitorWithThemes({
    entityTag,
  });

  const onRunScenario = (selectedAssetsItem: SelectedAssetItem) => {
    const workingListFilterOption = {
      key: WORKING_LIST_ID,
      name: t('common:filters.workingList'),
      value: {
        workingList: WORKING_LIST_ID,
      },
    };
    let scenarioTags: string[] = [];

    if (selectedAssetsItem.assetFilterValueKey === WORKING_LIST_ID) {
      scenarioTags = competitorsData?.map(c => c.company) ?? [];
    } else {
      const lists = useWatchlist.getState().lists;
      scenarioTags = getWatchlistEntitiesTags(lists, selectedAssetsItem.id);
    }

    if (!scenarioTags.some(tag => tag === entityTag)) {
      scenarioTags.push(entityTag);
    }

    saveWorkingList(scenarioTags);
    addScenarioFilterOption({
      filterKey: SCENARIO_ASSET_FILTER_KEY,
      option: workingListFilterOption,
    });

    const scenarioConditions = conditions.map(condition => ({
      snake: condition.snake,
      condition: conditionTypeToString[condition.condition] as ConditionType,
      value: Number(condition.value.toFixed(2)),
      label: '',
      entity: entityTag,
      duration: condition.duration,
      duration_unit: condition.duration_unit,
    }));
    resetActiveConditionFilterOptions();
    resetConditions();
    manuallySetCustomConditions(scenarioConditions);

    navigate(appPaths.scenario);
  };

  return {
    onRunScenario,
  };
};
